import React, { useEffect, useState, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "@store/StoreProvider";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ADMIN_NAV_ROUTES, isOperatorUserRole } from "@constants/adminNav";
import { Icon } from "@app/landing/Icon";
import { IconSprite } from "@app/landing/IconSprite";
import { LandingLogo } from "@app/landing/LandingLogo";
import { landingSectionLabel } from "@app/landing/sectionNav";
import { LangSwitcher } from "@components/LangSwitcher";
import {
  ADMIN_MOBILE_SIDEBAR_STATE_EVENT,
  ADMIN_TOGGLE_MOBILE_SIDEBAR_EVENT,
} from "@hooks/useAdminSidebar";
import { PanelLeftFilledIcon, PanelLeftIcon } from "@components/AdminMenuIcons";
import { t } from '@store/I18nStore'
import { SUPPORT_BOT_HANDLE, SUPPORT_BOT_URL, TELEGRAM_NEWS_URL, supportBotLabel } from '@constants/support'

const ADMIN_SHELL_PATHS = Object.values(ADMIN_NAV_ROUTES);

function isAdminShellPath(pathname: string): boolean {
  return ADMIN_SHELL_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

export const Header: React.FC = observer(() => {
  const { auth, i18n, ui, emailVerification } = useStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [adminSidebarOpen, setAdminSidebarOpen] = useState(false);
  const [theme, setTheme] = useState<'dark' | 'light'>(
    () => (localStorage.getItem('chatx_theme') as 'dark' | 'light') || 'dark',
  );

  const b1Ref = useRef<HTMLSpanElement>(null);
  const b2Ref = useRef<HTMLSpanElement>(null);
  const b3Ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('chatx_theme', theme);
  }, [theme]);

  useEffect(() => {
    const onSidebarState = (event: Event) => {
      const detail = (event as CustomEvent<{ open?: boolean }>).detail;
      setAdminSidebarOpen(Boolean(detail?.open));
    };
    window.addEventListener(ADMIN_MOBILE_SIDEBAR_STATE_EVENT, onSidebarState);
    return () => window.removeEventListener(ADMIN_MOBILE_SIDEBAR_STATE_EVENT, onSidebarState);
  }, []);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('menu-lock', menuOpen);
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    if (b1Ref.current) b1Ref.current.style.transform = menuOpen ? 'translateY(6px) rotate(45deg)' : '';
    if (b2Ref.current) b2Ref.current.style.opacity = menuOpen ? '0' : '1';
    if (b3Ref.current) b3Ref.current.style.transform = menuOpen ? 'translateY(-6px) rotate(-45deg)' : '';
    return () => {
      document.body.classList.remove('menu-lock');
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const isLoggedIn = auth.isAuthenticated;
  const isUsersPage = location.pathname === "/control";
  const isKb = location.pathname.startsWith('/knowledge-base');
  const showAdminMobileMenu = isLoggedIn && isAdminShellPath(location.pathname);
  const showControlHeaderLink =
    !isUsersPage &&
    !isOperatorUserRole(auth.currentUser?.role) &&
    auth.currentUser?.emailVerified !== false &&
    !emailVerification.blocked;

  const closeMenu = () => setMenuOpen(false);
  const toggleMenu = () => setMenuOpen((v) => !v);
  const toggleAdminMobileSidebar = () => {
    window.dispatchEvent(new CustomEvent(ADMIN_TOGGLE_MOBILE_SIDEBAR_EVENT));
  };

  const handleLogout = () => {
    closeMenu();
    auth.logout();
    navigate("/login");
  };

  const handleOpenSupport = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const tryOpen = (): boolean => {
      const anyWindow = window as unknown as Record<string, any>;
      const candidates = [
        anyWindow.TelegramSupportWidget,
        anyWindow.TelegramSupportBot,
        anyWindow.supportbot,
        anyWindow.SupportBot,
        anyWindow.telegramSupportBot,
      ];
      for (const api of candidates) {
        if (api && typeof api.open === "function") {
          api.open();
          return true;
        }
        if (api && typeof api.show === "function") {
          api.show();
          return true;
        }
        if (api && typeof api.toggle === "function") {
          api.toggle(true);
          return true;
        }
      }
      const launcher = document.querySelector(
        '[data-supportbot-open], [id*="support" i], [class*="support" i], [aria-label*="support" i]'
      ) as HTMLElement | null;
      if (launcher) {
        launcher.click();
        return true;
      }
      const frame = document.querySelector(
        'iframe[src*="code.telegramsupportbot.site"]'
      ) as HTMLIFrameElement | null;
      if (frame) {
        frame.focus();
        return true;
      }
      return false;
    };

    if (tryOpen()) {
      setTimeout(() => setMenuOpen(false), 50);
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;
    const interval = setInterval(() => {
      attempts += 1;
      if (tryOpen() || attempts >= maxAttempts) {
        clearInterval(interval);
        setTimeout(() => setMenuOpen(false), 50);
      }
    }, 200);
  };

  const navLinks = [
    { href: '/#features', label: landingSectionLabel((k) => i18n.t(k), 'features'), route: false, active: false },
    { href: '/#pricing', label: landingSectionLabel((k) => i18n.t(k), 'pricing'), route: false, active: false },
    { href: '/knowledge-base', label: i18n.t("nav.knowledge_base"), route: true, active: isKb },
    { href: '/#partners', label: landingSectionLabel((k) => i18n.t(k), 'partners'), route: false, active: false },
  ];

  return (
    <div
      className={`app-kb-header${ui.wsSiteConnectionBannerVisible ? ' app-header-wrap--banner' : ''}`}
      style={{
        marginTop: ui.wsSiteConnectionBannerVisible ? '42px' : '0',
        transition: 'top 0.2s ease, margin-top 0.2s ease',
      }}
    >
      <IconSprite />
      <style>
        {`
          .app-kb-header .app-header-desktop {
            display: none;
          }
          .app-kb-header .app-header-mobile-only {
            display: grid;
          }
          @media (min-width: 1024px) {
            .app-kb-header .app-header-desktop {
              display: flex !important;
            }
            .app-kb-header .app-header-mobile-only {
              display: none !important;
            }
          }
          .app-kb-header .app-header-admin-menu {
            display: none;
            place-items: center;
            width: 44px;
            height: 44px;
            border-radius: 12px;
            cursor: pointer;
            flex-shrink: 0;
          }
          .app-kb-header .app-header-admin-menu:active {
            transform: scale(0.9);
          }
          .app-kb-header .app-header-admin-menu-icon {
            display: block;
            width: 22px;
            height: 22px;
          }
          @media (max-width: 1023px) {
            .app-kb-header .app-header-nav--admin {
              display: grid;
              grid-template-columns: 44px minmax(0, 1fr) 44px;
              align-items: center;
              gap: 8px;
            }
            .app-kb-header .app-header-admin-menu {
              display: grid;
              grid-column: 1;
              justify-self: start;
            }
            .app-kb-header .app-header-nav--admin .app-header-logo {
              grid-column: 2;
              justify-self: center;
            }
            .app-kb-header .app-header-nav--admin .app-header-burger {
              grid-column: 3;
              justify-self: end;
            }
          }
          .app-kb-header .ic {
            width: 22px;
            height: 22px;
            stroke: currentColor;
            stroke-width: 1.9;
            fill: none;
            stroke-linecap: round;
            stroke-linejoin: round;
          }
          .app-kb-header .ic-sm { width: 17px; height: 17px; }
          .app-kb-header .ic-badge {
            display: inline-grid;
            place-items: center;
            width: 46px;
            height: 46px;
            border-radius: 14px;
            color: #8fbaff;
            background: linear-gradient(135deg, rgba(0, 89, 225, 0.22), rgba(99, 102, 241, 0.1));
            border: 1px solid rgba(59, 130, 246, 0.28);
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
          }
          .app-kb-header .land-mobile-menu {
            display: block;
            overflow: hidden;
            transition: opacity 0.35s ease, transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), visibility 0.35s;
            transform-origin: top center;
          }
          .app-kb-header .land-mobile-menu-scroll {
            height: 100%;
            overflow-x: hidden;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
            touch-action: pan-y;
            scrollbar-width: thin;
            scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
          }
          .app-kb-header .land-mobile-menu-scroll::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          .app-kb-header .land-mobile-menu-scroll::-webkit-scrollbar-track {
            background: transparent;
            border-radius: 10px;
            margin: 4px;
          }
          .app-kb-header .land-mobile-menu-scroll::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.2);
            border-radius: 10px;
          }
          .app-kb-header .land-mobile-menu-scroll::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.3);
          }
          :root[data-theme="light"] .app-kb-header .land-mobile-menu-scroll {
            scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
          }
          :root[data-theme="light"] .app-kb-header .land-mobile-menu-scroll::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.2);
          }
          :root[data-theme="light"] .app-kb-header .land-mobile-menu-scroll::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.3);
          }
          .app-kb-header .menu-closed {
            opacity: 0;
            transform: scale(1.04) translateY(-8px);
            pointer-events: none;
            visibility: hidden;
          }
          .app-kb-header .menu-open {
            opacity: 1;
            transform: scale(1) translateY(0);
            visibility: visible;
            pointer-events: auto;
          }
          .app-kb-header .menu-open .menu-item {
            animation: app-kb-menu-item 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
          }
          .app-kb-header .menu-open .menu-item:nth-child(2) { animation-delay: 0.04s; }
          .app-kb-header .menu-open .menu-item:nth-child(3) { animation-delay: 0.08s; }
          .app-kb-header .menu-open .menu-item:nth-child(4) { animation-delay: 0.12s; }
          @keyframes app-kb-menu-item {
            from { opacity: 0; transform: translateY(22px); }
            to { opacity: 1; transform: none; }
          }
          .app-kb-header .lang-wrap {
            position: relative;
          }
          .app-kb-header .lang-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            height: 42px;
            padding: 0 12px;
            border-radius: 12px;
          }
          .app-kb-header .lang-btn svg {
            width: 17px;
            height: 17px;
            flex-shrink: 0;
          }
          .app-kb-header .lang-btn .lang-code {
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 0.04em;
            line-height: 1;
          }
          .app-kb-header .lang-btn .lang-caret {
            width: 14px;
            height: 14px;
            opacity: 0.7;
            transition: transform 0.2s ease;
          }
          .app-kb-header .lang-btn[aria-expanded="true"] .lang-caret {
            transform: rotate(180deg);
            opacity: 1;
          }
          .app-kb-header .lang-menu {
            position: absolute;
            right: 0;
            top: calc(100% + 8px);
            min-width: 188px;
            padding: 6px;
            border-radius: 14px;
            backdrop-filter: blur(16px);
            -webkit-backdrop-filter: blur(16px);
            display: none;
            z-index: 40;
          }
          .app-kb-header .lang-menu.open {
            display: block;
            animation: app-kb-lang-menu 0.18s ease;
          }
          @keyframes app-kb-lang-menu {
            from { opacity: 0; transform: translateY(-4px); }
            to { opacity: 1; transform: none; }
          }
          .app-kb-header .lang-item {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 10px 12px;
            border: none;
            border-radius: 10px;
            background: transparent;
            font-size: 14px;
            font-weight: 600;
            text-align: left;
          }
          .app-kb-header .lang-item .lang-item-short {
            width: 30px;
            height: 30px;
            display: grid;
            place-items: center;
            border-radius: 8px;
            font-size: 11px;
            font-weight: 800;
            letter-spacing: 0.04em;
            color: var(--brand-1);
            background: color-mix(in srgb, var(--brand-1) 14%, transparent);
            border: 1px solid color-mix(in srgb, var(--brand-1) 28%, transparent);
            flex-shrink: 0;
          }
          .app-kb-header .lang-item .lang-item-label {
            flex: 1;
            min-width: 0;
          }
          .app-kb-header .lang-item .lang-item-check {
            width: 16px;
            height: 16px;
            color: var(--brand-1);
            opacity: 0;
            flex-shrink: 0;
          }
          .app-kb-header .lang-item.active .lang-item-check {
            opacity: 1;
          }
        `}
      </style>

      <header className="z-50">
        <div className="glass">
          <nav
            className={`app-header-nav${
              showAdminMobileMenu ? ' app-header-nav--admin' : ''
            }`}
          >
            {showAdminMobileMenu && (
              <button
                type="button"
                className="app-header-admin-menu"
                onClick={toggleAdminMobileSidebar}
                aria-label={adminSidebarOpen ? t('nav.close_menu') : t('app.actions.open_menu')}
                aria-expanded={adminSidebarOpen}
              >
                {adminSidebarOpen ? (
                  <PanelLeftFilledIcon className="app-header-admin-menu-icon" size={22} />
                ) : (
                  <PanelLeftIcon className="app-header-admin-menu-icon" size={22} />
                )}
              </button>
            )}

            <Link to="/" className="app-header-logo app-header-brand group shrink-0" aria-label={t('nav.home')}>
              <LandingLogo className="group-hover:scale-105 transition-transform" />
            </Link>

            <ul className="app-header-desktop app-header-links">
              {navLinks.map((link) => (
                <li key={link.href}>
                  {link.route ? (
                    <Link
                      to={link.href}
                      className={`app-header-link inline-flex items-center gap-1.5${
                        link.active ? ' is-active' : ''
                      }`}
                    >
                      {link.active && <span className="w-1.5 h-1.5 rounded-full bg-brand2" />}
                      {link.label}
                    </Link>
                  ) : (
                    <a href={link.href} className="app-header-link">
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>

            <div className="app-header-desktop app-header-actions">
              <div className="appbar-right">
                <button
                  id="themeBtn"
                  className="theme-toggle cursor-pointer"
                  type="button"
                  onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                  aria-label={theme === 'light' ? i18n.t('widget.theme.light_theme') : i18n.t('widget.theme.dark_theme')}
                >
                  <span className="theme-dot" />
                  <span id="themeLabel">{theme === 'light' ? i18n.t('widget.theme.light_theme') : i18n.t('widget.theme.dark_theme')}</span>
                </button>
                <LangSwitcher />
              </div>

              {!isLoggedIn ? (
                <>
                  <button
                    type="button"
                    onClick={(e) => handleOpenSupport(e)}
                    className="app-header-ghost-btn inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl cursor-pointer"
                  >
                    <Icon id="ic-message" className="ic ic-sm text-brand2" />
                    {i18n.t("nav.help")}
                  </button>
                  <Link
                    to="/login"
                    className="app-header-link-btn px-4 py-2.5 text-sm font-semibold transition"
                  >
                    {i18n.t("app.actions.sign_in")}
                  </Link>
                  <Link
                    to="/register"
                    className="px-5 py-2.5 text-sm font-semibold rounded-xl bg-brand hover:bg-blue-600 shadow-lg shadow-brand/30 transition active:scale-95 text-white"
                  >
                    {i18n.t("auth.sign_up")}
                  </Link>
                </>
              ) : (
                <>
                  {showControlHeaderLink && (
                    <Link
                      to="/control"
                      className="px-5 py-2.5 text-sm font-semibold rounded-xl bg-brand hover:bg-blue-600 shadow-lg shadow-brand/30 transition active:scale-95 text-white"
                    >
                      {i18n.t("nav.management")}
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="app-header-ghost-btn app-header-logout-btn inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl hover:border-red-400/50 cursor-pointer"
                  >
                    <Icon id="ic-logout" className="ic ic-sm text-brand2" />
                    {i18n.t("app.actions.sign_out")}
                  </button>
                </>
              )}
            </div>

            <button
              type="button"
              aria-label={i18n.t('nav.menu')}
              className="app-header-mobile-only app-header-burger place-items-center w-11 h-11 rounded-xl active:scale-90 transition"
              onClick={toggleMenu}
            >
              <span className="block w-5 space-y-1.5">
                <span ref={b1Ref} className="app-header-burger-bar block h-[2px] rounded transition-all" />
                <span ref={b2Ref} className="app-header-burger-bar block h-[2px] rounded transition-all" />
                <span ref={b3Ref} className="app-header-burger-bar block h-[2px] rounded transition-all" />
              </span>
            </button>
          </nav>
        </div>
      </header>

      <div className={`app-header-mobile-only land-mobile-menu fixed inset-0 z-[60] ${menuOpen ? 'menu-open' : 'menu-closed'}`}>
        <div className="land-mobile-menu-bg absolute inset-0 backdrop-blur-xl" />
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-brand/12 via-transparent to-indigo-600/12" />

        <div className="land-mobile-menu-scroll custom-scroll relative h-full min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain">
          <div
            className="land-mobile-menu-panel min-h-full w-full max-w-md mx-auto flex flex-col px-6 sm:px-7"
            style={{
              paddingTop: 'max(1.1rem, env(safe-area-inset-top))',
              paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
            }}
          >
          <div className="menu-item flex items-center justify-between py-1">
            <LandingLogo />
            <button
              type="button"
              aria-label={i18n.t('nav.close_menu')}
              className="mm-close grid place-items-center w-11 h-11 rounded-xl active:scale-90 transition"
              onClick={closeMenu}
            >
              <Icon id="ic-x" />
            </button>
          </div>

          <div className="menu-item mt-7 mb-1 text-[11px] uppercase tracking-[0.2em] text-brand2 font-semibold">
            {i18n.t('nav.navigation')}
          </div>

          <nav className="flex flex-col">
            {navLinks.map((link) =>
              link.route ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="menu-item mm-nav-link group flex items-center justify-between text-[1.35rem] font-extrabold py-3"
                  onClick={closeMenu}
                >
                  {link.label}
                  <Icon id="ic-arrow-right" className="ic mm-arrow group-active:text-brand2 transition" />
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className="menu-item mm-nav-link group flex items-center justify-between text-[1.35rem] font-extrabold py-3"
                  onClick={closeMenu}
                >
                  {link.label}
                  <Icon id="ic-arrow-right" className="ic mm-arrow group-active:text-brand2 transition" />
                </a>
              ),
            )}
          </nav>

          <div className="menu-item mt-7">
            <div className="text-[11px] uppercase tracking-[0.2em] text-brand2 font-semibold mb-2">{i18n.t('nav.settings')}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="mm-chip inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl"
              >
                <Icon id={theme === 'light' ? 'ic-sun' : 'ic-moon'} className="ic ic-sm text-brand2" />
                {theme === 'light' ? i18n.t('widget.theme.light_theme') : i18n.t('widget.theme.dark_theme')}
              </button>
              <LangSwitcher variant="pills" />
            </div>
          </div>

          <div className="menu-item mt-7">
            <div className="text-[11px] uppercase tracking-[0.2em] text-brand2 font-semibold mb-2">{i18n.t('nav.footer.contact')}</div>
            <button
              type="button"
              onClick={(e) => handleOpenSupport(e)}
              className="mm-row w-full flex items-center gap-3 py-3 active:opacity-70 transition text-left"
            >
              <span className="ic-badge shrink-0" style={{ width: 42, height: 42 }}>
                <Icon id="ic-message" className="ic ic-sm" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-sm">{i18n.t('nav.help')}</span>
                <span className="block text-xs mm-muted">{i18n.t('landing.features.one_click_support')}</span>
              </span>
              <Icon id="ic-arrow-right" className="ic ic-sm mm-arrow shrink-0" />
            </button>
            <a
              href={SUPPORT_BOT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 py-3 active:opacity-70 transition"
            >
              <span className="ic-badge shrink-0" style={{ width: 42, height: 42 }}>
                <Icon id="ic-message" className="ic ic-sm" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-sm">{supportBotLabel((k) => i18n.t(k), i18n.language)}</span>
                <span className="block text-xs mm-muted">{SUPPORT_BOT_HANDLE}</span>
              </span>
              <Icon id="ic-arrow-right" className="ic ic-sm mm-arrow shrink-0" />
            </a>
            <a
              href={TELEGRAM_NEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 py-3 active:opacity-70 transition"
            >
              <span className="ic-badge shrink-0" style={{ width: 42, height: 42 }}>
                <Icon id="ic-send" className="ic ic-sm" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-sm">{i18n.t('nav.footer.telegram_channel')}</span>
                <span className="block text-xs mm-muted">@chatx</span>
              </span>
              <Icon id="ic-arrow-right" className="ic ic-sm mm-arrow shrink-0" />
            </a>
          </div>

          <div className="flex-1 min-h-4" />

          <div className="menu-item flex flex-col gap-3">
            {!isLoggedIn ? (
              <>
                <Link
                  to="/login"
                  className="mm-ghost-btn text-center py-4 rounded-2xl font-semibold text-lg active:scale-95 transition"
                  onClick={closeMenu}
                >
                  {i18n.t("app.actions.sign_in")}
                </Link>
                <Link
                  to="/register"
                  className="text-center py-4 rounded-2xl bg-brand hover:bg-blue-600 font-semibold text-lg shadow-lg shadow-brand/30 active:scale-95 transition text-white"
                  onClick={closeMenu}
                >
                  {i18n.t("auth.sign_up")} →
                </Link>
              </>
            ) : (
              <>
                {showControlHeaderLink && (
                  <Link
                    to="/control"
                    className="text-center py-4 rounded-2xl bg-brand hover:bg-blue-600 font-semibold text-lg shadow-lg shadow-brand/30 active:scale-95 transition text-white"
                    onClick={closeMenu}
                  >
                    {i18n.t("nav.management")}
                  </Link>
                )}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="mm-ghost-btn text-center py-4 rounded-2xl font-semibold text-lg active:scale-95 transition"
                >
                  {i18n.t("app.actions.sign_out")}
                </button>
              </>
            )}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
});
